import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Timeline, TimelineEvent, TimelineFilters } from '../history/shared/Timeline';
import { FilterDefinition, FilterValues } from '../history/shared/FilterPanel';
import { UserRole, RoleOption } from '../history/shared/RoleSwitcher';
import { SearchQuery } from '../history/shared/SearchBar';
import { SearchEngine } from '../../services/history/SearchEngine';
import { AnalyticsEngine } from '../../services/history/AnalyticsEngine';
import { ESortOrder, ICampaign, ICampaignListParams } from 'core/features/campaigns/types';
import { useHistoryCampaigns } from 'core';
import { SkeletonSwitch, TimeLineItemSkeleton } from 'skeleton';
import Pagination from 'components/ui/pagination';

export interface CampaignHistoryData {
    campaignId: string;
    campaignName: string;
    statusFrom?: string;
    statusTo?: string;
    assigneeFrom?: string;
    assigneeTo?: string;
    campaignType: string;
    performance?: {
        openRate?: number;
        clickRate?: number;
        conversionRate?: number;
        roi?: number;
    };
    abTestResults?: {
        variantA: string;
        variantB: string;
        winner?: string;
        confidenceLevel?: number;
    };
    metrics?: Record<string, number>;
}

export interface EnhancedCampaignHistoryProps {
    className?: string;
    onEventClick?: (event: TimelineEvent) => void;
    userRole?: UserRole;
    userPermissions?: string[];
    enableRealTimeUpdates?: boolean;
    /** Controlled role — lifted from parent (HistoryView) */
    currentRole?: UserRole;
    /** Notify parent when role changes */
    onRoleChange?: (role: UserRole) => void;
}

// Role-based configurations for campaign history
export const campaignHistoryRoles: RoleOption[] = [
    {
        role: 'customer',
        label: 'Customer',
        description: 'View your own campaigns and basic metrics',
        icon: '👤',
        permissions: ['view_own_campaigns'],
        available: true
    },
    {
        role: 'agent',
        label: 'Agent',
        description: 'View assigned campaigns with performance data',
        icon: '👨‍💼',
        permissions: ['view_assigned_campaigns', 'view_performance_metrics'],
        available: true
    },
    {
        role: 'manager',
        label: 'Manager',
        description: 'View team campaigns with analytics and trends',
        icon: '👩‍💼',
        permissions: ['view_team_campaigns', 'view_analytics', 'export_data'],
        available: true
    },
    {
        role: 'admin',
        label: 'Admin',
        description: 'Full access to all campaigns and system data',
        icon: '⚙️',
        permissions: ['view_all_campaigns', 'view_system_data', 'manage_campaigns'],
        available: true
    }
];

// Filter definitions for campaign history
const getCampaignFilters = (role: UserRole): FilterDefinition[] => {
    const baseFilters: FilterDefinition[] = [
        { key: 'dateRange', type: 'date', label: 'Date Range' },
        {
            key: 'campaignType',
            type: 'multiselect',
            label: 'Campaign Type',
            options: [
                { value: 'promotional', label: 'Promotional', count: 45 },
                { value: 'automated', label: 'Automated', count: 23 },
                { value: 'announcement', label: 'Announcement', count: 12 },
                { value: 'newsletter', label: 'Newsletter', count: 67 }
            ]
        },
        {
            key: 'status',
            type: 'multiselect',
            label: 'Campaign Status',
            options: [
                { value: 'draft', label: 'Draft', count: 8 },
                { value: 'active', label: 'Active', count: 15 },
                { value: 'completed', label: 'Completed', count: 89 },
                { value: 'paused', label: 'Paused', count: 3 }
            ]
        }
    ];

    if (role === 'agent' || role === 'manager' || role === 'admin') {
        baseFilters.push({
            key: 'performance', type: 'range', label: 'Open Rate (%)', min: 0, max: 100
        });
    }

    if (role === 'manager' || role === 'admin') {
        baseFilters.push(
            {
                key: 'assignee',
                type: 'multiselect',
                label: 'Assigned To',
                options: [
                    { value: 'john.doe', label: 'John Doe', count: 12 },
                    { value: 'jane.smith', label: 'Jane Smith', count: 18 },
                    { value: 'mike.wilson', label: 'Mike Wilson', count: 9 }
                ]
            },
            {
                key: 'team',
                type: 'select',
                label: 'Team',
                options: [
                    { value: 'marketing', label: 'Marketing Team', count: 45 },
                    { value: 'sales', label: 'Sales Team', count: 23 },
                    { value: 'support', label: 'Support Team', count: 8 }
                ]
            }
        );
    }

    if (role === 'admin') {
        baseFilters.push({
            key: 'systemEvents',
            type: 'multiselect',
            label: 'System Events',
            options: [
                { value: 'auto_optimization', label: 'Auto Optimization', count: 15 },
                { value: 'ai_intervention', label: 'AI Intervention', count: 8 },
                { value: 'system_error', label: 'System Error', count: 2 }
            ]
        });
    }

    return baseFilters;
};

export const EnhancedCampaignHistory: React.FC<EnhancedCampaignHistoryProps> = ({
    onEventClick,
    userRole = 'admin',
    enableRealTimeUpdates = false,
    currentRole: controlledRole,
    onRoleChange: notifyRoleChange,
}) => {
    // Internal role state — used only when not controlled externally
    const [internalRole, setInternalRole] = useState<UserRole>(userRole);
    const currentRole = controlledRole ?? internalRole;

    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [filters, setFilters] = useState<FilterValues>({});
    const [timelineFilters, setTimelineFilters] = useState<TimelineFilters>({});
    const [isLoading, setIsLoading] = useState(false);
    const [_analytics, setAnalytics] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Initialize services
    const searchEngine = useMemo(() => SearchEngine.getInstance(), []);
    const analyticsEngine = useMemo(() => AnalyticsEngine.getInstance(), []);

    const _availableFilters = useMemo(() => getCampaignFilters(currentRole), [currentRole]);

    /******** Campaign Query **********/
    const [campaignFilter, setCampaignFilter] = React.useState<ICampaignListParams>({
        page: 1,
        active: undefined,
        progress: undefined,
        pc: undefined,
        comp: undefined,
        terminated: undefined,
        sortBy: "created_at",
        order: ESortOrder.Desc,
    });

    const { data: campaignData, isLoading: campaignLoading } =
        useHistoryCampaigns<ICampaign>(campaignFilter);

    useEffect(() => {
        if (!isLoading) {
            console.log("Campaign Data--->", campaignData);
        }
    }, [campaignData]);

    // Filter events based on current role and filters
    const filteredEvents = useMemo(() => {
        let filtered = [...events];

        switch (currentRole) {
            case 'customer':
                filtered = filtered.filter(event =>
                    event.metadata?.createdBy === 'john.doe@company.com' ||
                    event.metadata?.campaignType === 'promotional'
                );
                break;
            case 'agent':
                filtered = filtered.filter(event =>
                    event.metadata?.campaignType || event.type.includes('campaign')
                );
                break;
        }

        if (filters.campaignType && filters.campaignType.length > 0) {
            filtered = filtered.filter(event =>
                filters.campaignType.includes(event.metadata?.campaignType)
            );
        }
        if (filters.status && filters.status.length > 0) {
            filtered = filtered.filter(event =>
                filters.status.includes(event.metadata?.status)
            );
        }
        if (filters.performance) {
            filtered = filtered.filter(event => {
                const openRate = event.metadata?.openRate;
                if (openRate !== undefined) {
                    const min = filters.performance.min || 0;
                    const max = filters.performance.max || 100;
                    return openRate >= min && openRate <= max;
                }
                return true;
            });
        }

        return filtered;
    }, [events, currentRole, filters]);

    const _handleRoleChange = useCallback((newRole: UserRole) => {
        setInternalRole(newRole);
        notifyRoleChange?.(newRole);
        setFilters({});
        setTimelineFilters({});
    }, [notifyRoleChange]);

    const _handleFiltersChange = useCallback((newFilters: FilterValues) => {
        setFilters(newFilters);
        const tFilters: TimelineFilters = {};
        if (searchQuery) tFilters.searchText = searchQuery;
        setTimelineFilters(tFilters);
    }, [searchQuery]);

    const _handleSearch = useCallback(async (query: SearchQuery) => {
        setIsLoading(true);
        setSearchQuery(query.text);
        try {
            const searchResults = await searchEngine.search({
                text: query.text,
                contexts: ['campaign'],
                filters: { dateRange: filters.dateRange, eventTypes: filters.eventTypes },
                userRole: currentRole,
                pagination: { page: 1, limit: 50 }
            });
            const searchEvents = searchResults.results?.map((result: any) => ({
                id: result.entry.id,
                type: result.entry.type,
                timestamp: result.entry.timestamp,
                title: result.entry.title,
                description: result.entry.description,
                icon: '🔍',
                color: '#6B7280',
                metadata: result.entry.data
            })) || [];
            setEvents(searchEvents);
        } catch (error) {
            console.error('Search failed:', error);
        } finally {
            setIsLoading(false);
        }
    }, [searchEngine, currentRole, filters]);

    const handleEventClick = useCallback((event: TimelineEvent) => {
        if (onEventClick) {
            onEventClick(event);
        } else {
            console.log('Campaign event clicked:', event);
        }
    }, [onEventClick]);

    useEffect(() => {
        const loadAnalytics = async () => {
            if (currentRole === 'manager' || currentRole === 'admin') {
                try {
                    const analyticsData = await analyticsEngine.getMetrics('campaign', {
                        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                        end: new Date()
                    });
                    setAnalytics(analyticsData);
                } catch (error) {
                    console.error('Failed to load analytics:', error);
                }
            }
        };
        loadAnalytics();
    }, [currentRole, analyticsEngine]);

    useEffect(() => {
        if (!enableRealTimeUpdates) return;
        const interval = setInterval(() => {
            if (Math.random() < 0.1) {
                const newEvent: TimelineEvent = {
                    id: `camp_${Date.now()}`,
                    type: 'performance_update',
                    timestamp: new Date(),
                    title: 'Real-time Performance Update',
                    description: 'Campaign metrics updated in real-time',
                    icon: '📊',
                    color: '#10B981',
                    metadata: {
                        campaignId: 'camp_001',
                        campaignName: 'Summer Sale 2024',
                        campaignType: 'promotional',
                        openRate: Math.random() * 100,
                        clickRate: Math.random() * 20,
                        realTime: true
                    }
                };
                setEvents(prev => [newEvent, ...prev]);
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [enableRealTimeUpdates]);

    // Custom event renderers — all preserved exactly
    const customRenderers = useMemo(() => ({
        new_campaign_created: (event: TimelineEvent) => (
            <div className="space-y-3">
                <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Type:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.campaignType}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Target:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.targetAudience}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Reach:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.estimatedReach?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Created by:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.createdBy}</span>
                    </div>
                </div>
            </div>
        ),
        campaign_created: (event: TimelineEvent) => (
            <div className="space-y-3">
                <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Type:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.campaignType}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Target:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.targetAudience}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Reach:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.estimatedReach?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Created by:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.createdBy}</span>
                    </div>
                </div>
            </div>
        ),
        campaign_launched: (event: TimelineEvent) => (
            <div className="space-y-3">
                <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Campaign:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.campaignName}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Actual Reach:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.actualReach?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">A/B Testing:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.abTestEnabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Launched by:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.launchedBy}</span>
                    </div>
                </div>
            </div>
        ),
        performance_update: (event: TimelineEvent) => (
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    {event.metadata?.openRate && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                            <span className="text-xs text-blue-600 dark:text-blue-400 block mb-1">Open Rate</span>
                            <span className="text-lg font-semibold text-blue-700 dark:text-blue-300">{Number(event.metadata.openRate).toFixed(1)}%</span>
                        </div>
                    )}
                    {event.metadata?.clickRate && (
                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                            <span className="text-xs text-green-600 dark:text-green-400 block mb-1">Click Rate</span>
                            <span className="text-lg font-semibold text-green-700 dark:text-green-300">{Number(event.metadata.clickRate).toFixed(1)}%</span>
                        </div>
                    )}
                    {event.metadata?.conversionRate && (
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                            <span className="text-xs text-purple-600 dark:text-purple-400 block mb-1">Conversion Rate</span>
                            <span className="text-lg font-semibold text-purple-700 dark:text-purple-300">{Number(event.metadata.conversionRate).toFixed(1)}%</span>
                        </div>
                    )}
                    {event.metadata?.revenue && (
                        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                            <span className="text-xs text-amber-600 dark:text-amber-400 block mb-1">Revenue</span>
                            <span className="text-lg font-semibold text-amber-700 dark:text-amber-300">${event.metadata.revenue.toLocaleString()}</span>
                        </div>
                    )}
                </div>
                {event.metadata?.campaignName && (
                    <div className="flex items-center gap-2 pt-2 border-t border-line dark:border-gray-700">
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata.campaignName}</span>
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-xs text-content-secondary dark:text-gray-400 rounded">{event.metadata.campaignType}</span>
                    </div>
                )}
            </div>
        ),
        ab_test_completed: (event: TimelineEvent) => (
            <div className="space-y-3">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-green-700 dark:text-green-300 font-medium">Winner:</span>
                        <span className="text-base font-semibold text-green-800 dark:text-green-200">{event.metadata?.winner}</span>
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">
                        {Number(event.metadata?.confidenceLevel).toFixed(1)}% confidence
                    </div>
                </div>
                {event.metadata?.liftPercentage && (
                    <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <span className="text-sm text-blue-700 dark:text-blue-300">Performance Lift:</span>
                        <span className="text-lg font-semibold text-blue-800 dark:text-blue-200">+{Number(event.metadata.liftPercentage).toFixed(1)}%</span>
                    </div>
                )}
                <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Test Duration:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.testDuration}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Sample Size:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.sampleSize?.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        ),
        campaign_optimized: (event: TimelineEvent) => (
            <div className="space-y-3">
                <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Optimized by:</span>
                        <span className="text-sm font-medium text-purple-600 dark:text-purple-400">{event.metadata?.optimizedBy}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-content-secondary dark:text-gray-400">Expected Improvement:</span>
                        <span className="text-sm font-medium text-content dark:text-white">{event.metadata?.expectedImprovement}</span>
                    </div>
                    {event.metadata?.changes && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Changes Applied:</span>
                            <ul className="space-y-1">
                                {event.metadata.changes.map((change: string, index: number) => (
                                    <li key={index} className="text-sm text-content-secondary dark:text-gray-400 flex items-start gap-2">
                                        <span className="text-green-500 dark:text-green-400 mt-0.5">✓</span>
                                        <span>{change}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        )
    }), []);

    return (
        <>
            <SkeletonSwitch
                isLoading={campaignLoading}
                skeleton={
                    <div className="pt-4 bg-gray-50/60 dark:bg-white/[0.02]">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <TimeLineItemSkeleton key={i} />
                        ))}
                    </div>
                }
            >
                <div className="pt-4">
                    <Timeline
                    campaignData={campaignData}
                    events={filteredEvents}
                    context="campaign"
                    filters={timelineFilters}
                    onEventClick={handleEventClick}
                    onFilterChange={setTimelineFilters}
                    customRenderers={customRenderers}
                    showAnalytics={currentRole === 'manager' || currentRole === 'admin'}
                    height={500}
                    className="w-full"
                    itemSize={280}
                />
                </div>
                {campaignData?.total > 0 && campaignLoading === false && (
                    <Pagination
                        totalEntries={campaignData.total || 0}
                        entriesPerPage={10}
                        currentPage={campaignFilter.page || 1}
                        onPageChange={(page) => setCampaignFilter(prev => ({ ...prev, page }))}
                        maxVisiblePages={7}
                        className="justify-end mt-4"
                    />
                )}
            </SkeletonSwitch>
        </>
    );
};

export default EnhancedCampaignHistory;
